import React, { useState, useEffect } from "react";
import { Col, Row, Button } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import { GlobalSet } from "../../global/GlobalProvider";

const TeamInvestReport = () => {

  const { CurrencySymbol } = GlobalSet();

  // =========================================================
  // STATES
  // =========================================================

  const [investData, setInvestData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [totalindt, totalinvest] = useState({});

  const [filterType, setFilterType] = useState("total");
  const [search, setSearch] = useState("");

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [level, setLevel] = useState("");
  const [totalAmount, setTotalAmount] = useState(0);

  const [loading, setLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState("active");


  // =========================================================
  // IMPORTANT:
  // FORMAT DATE USING LOCAL DATE
  //
  // DO NOT USE:
  // date.toISOString()
  //
  // Because toISOString() converts local date to UTC
  // and can change 10 date into 9 date.
  // =========================================================

  const formatLocalDate = (date) => {

    if (!date) {
      return "";
    }

    const year = date.getFullYear();

    const month = String(
      date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
      date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };


  // =========================================================
  // FETCH DATA
  // =========================================================

  const fetchData = async (
    type = "total",
    sDate = null,
    eDate = null
  ) => {

    const auth_token =
      localStorage.getItem("auth_token");

    const selectedCurrency =
      localStorage.getItem("selectedCurrency") || "USD";


    // ---------------------------------------------------------
    // Convert DatePicker date to YYYY-MM-DD
    // WITHOUT timezone conversion
    // ---------------------------------------------------------

    const formattedStartDate =
      formatLocalDate(sDate);

    const formattedEndDate =
      formatLocalDate(eDate);


    // ---------------------------------------------------------
    // API PAYLOAD
    // ---------------------------------------------------------

    const payload = {

      key_s:
        "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9",

      currency: selectedCurrency,

      level: level ? level : "",

      selete_date: type,

      filter: statusFilter,

      start_date: formattedStartDate,

      to_date: formattedEndDate,
    };


    // =========================================================
    // DEBUG
    // Open F12 -> Console
    // =========================================================

    console.log("======================================");

    console.log(
      "Selected Start Date Object:",
      sDate
    );

    console.log(
      "Selected End Date Object:",
      eDate
    );

    console.log(
      "API START DATE:",
      formattedStartDate
    );

    console.log(
      "API TO DATE:",
      formattedEndDate
    );

    console.log(
      "FULL API PAYLOAD:",
      payload
    );

    console.log("======================================");


    // ---------------------------------------------------------
    // Check Login
    // ---------------------------------------------------------

    if (!auth_token) {
      return;
    }


    setLoading(true);


    try {

      // -------------------------------------------------------
      // API REQUEST
      // -------------------------------------------------------

      const res = await axios.post(
        "/team-statement",
        payload,
        {
          headers: {
            Authorization: `Bearer ${auth_token}`,
          },
        }
      );


      console.log(
        "TEAM STATEMENT API RESPONSE:",
        res.data
      );


      // -------------------------------------------------------
      // SUCCESS
      // -------------------------------------------------------

      if (res.data.status === "success") {

        setInvestData(
          res.data.data || []
        );

        setFilteredData(
          res.data.data || []
        );

        totalinvest(
          res.data.total_invest || {}
        );

        setTotalAmount(
          Number(res.data.total_amount || 0)
        );

      } else {

        setInvestData([]);

        setFilteredData([]);

        totalinvest({});

        setTotalAmount(0);
      }


    } catch (err) {

      console.error(
        "TEAM STATEMENT API ERROR:",
        err
      );

    } finally {

      setLoading(false);
    }
  };


  // =========================================================
  // STATUS CHANGE
  // =========================================================

  useEffect(() => {

    fetchData(
      filterType,
      startDate,
      endDate
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [statusFilter]);


  // =========================================================
  // TODAY / WEEK / MONTH / YEAR
  // =========================================================

  const handleFilterClick = (type) => {

    setFilterType(type);

    // Predefined filter does not need custom date
    fetchData(
      type,
      null,
      null
    );
  };


  // =========================================================
  // CUSTOM DATE RANGE FILTER
  // =========================================================

  const handleDateRangeFilter = () => {

    if (!startDate || !endDate) {

      alert(
        "Please select both start and end dates"
      );

      return;
    }


    // Prevent wrong date range

    if (startDate > endDate) {

      alert(
        "Start Date cannot be greater than End Date"
      );

      return;
    }


    setFilterType("custom");


    // IMPORTANT:
    // Pass DatePicker Date objects directly.
    // formatLocalDate() handles them inside fetchData.

    fetchData(
      "custom",
      startDate,
      endDate
    );
  };


  // =========================================================
  // USERNAME SEARCH
  // =========================================================

  const searchedData = filteredData.filter(
    (item) => {

      const username =
        item.username || "";

      return username
        .toLowerCase()
        .includes(
          search.toLowerCase()
        );
    }
  );


  // =========================================================
  // TEAM TOTALS
  // =========================================================

  const sumA =
    Number(totalindt?.sumA || 0);

  const sumB =
    Number(totalindt?.sumB || 0);

  const sumC =
    Number(totalindt?.sumC || 0);

  const team_level_others =
    Number(
      totalindt?.team_level_others || 0
    );

  const sumtotal =
    Number(totalindt?.sumtotal || 0);


  // =========================================================
  // UI
  // =========================================================

  return (

    <div className="container-fluid py-4">

      {/* =====================================================
          PAGE TITLE
      ===================================================== */}

      <h3 className="text-dark mb-4">
        Team Invest Report
      </h3>


      {/* =====================================================
          FILTER CARD
      ===================================================== */}

      <div className="card p-3 mb-4 shadow-sm">

        <Row className="g-2 align-items-center">

          {/* =================================================
              PREDEFINED FILTERS
          ================================================= */}

          <Col
            md="8"
            className="d-flex flex-wrap gap-2"
          >

            {/* TODAY */}

            <Button
              variant={
                filterType === "today"
                  ? "warning"
                  : "secondary"
              }
              onClick={() =>
                handleFilterClick("today")
              }
            >
              Today Invest
            </Button>


            {/* WEEK */}

            <Button
              variant={
                filterType === "week"
                  ? "warning"
                  : "secondary"
              }
              onClick={() =>
                handleFilterClick("week")
              }
            >
              Weekly Invest
            </Button>


            {/* MONTH */}

            <Button
              variant={
                filterType === "month"
                  ? "warning"
                  : "secondary"
              }
              onClick={() =>
                handleFilterClick("month")
              }
            >
              This Month
            </Button>


            {/* YEAR */}

            <Button
              variant={
                filterType === "year"
                  ? "warning"
                  : "secondary"
              }
              onClick={() =>
                handleFilterClick("year")
              }
            >
              This Year
            </Button>


            {/* =================================================
                ACTIVE
            ================================================= */}

            <div className="form-check form-check-inline">

              <input
                className="form-check-input"
                type="radio"
                name="statusFilter"
                id="statusActive"
                value="active"

                checked={
                  statusFilter === "active"
                }

                onChange={(e) =>
                  setStatusFilter(
                    e.target.value
                  )
                }
              />

              <label
                className="form-check-label text-dark"
                htmlFor="statusActive"
              >
                Active
              </label>

            </div>


            {/* =================================================
                INACTIVE
            ================================================= */}

            <div className="form-check form-check-inline">

              <input
                className="form-check-input"
                type="radio"
                name="statusFilter"
                id="statusInactive"
                value="inactive"

                checked={
                  statusFilter === "inactive"
                }

                onChange={(e) =>
                  setStatusFilter(
                    e.target.value
                  )
                }
              />

              <label
                className="form-check-label text-dark"
                htmlFor="statusInactive"
              >
                Inactive
              </label>

            </div>


            {/* =================================================
                ALL
            ================================================= */}

            <div className="form-check form-check-inline">

              <input
                className="form-check-input"
                type="radio"
                name="statusFilter"
                id="statusAll"
                value="all"

                checked={
                  statusFilter === "all"
                }

                onChange={(e) =>
                  setStatusFilter(
                    e.target.value
                  )
                }
              />

              <label
                className="form-check-label text-dark"
                htmlFor="statusAll"
              >
                All
              </label>

            </div>

          </Col>


          {/* =================================================
              DATE RANGE
          ================================================= */}

          <Col
            md="4"
            className="d-flex align-items-center gap-2 justify-content-end"
          >

            {/* START DATE */}

            <DatePicker

              selected={startDate}

              onChange={(date) => {

                console.log(
                  "START DATE SELECTED:",
                  date
                );

                console.log(
                  "START LOCAL DATE:",
                  formatLocalDate(date)
                );

                setStartDate(date);
              }}

              dateFormat="yyyy-MM-dd"

              placeholderText="Start Date"

              className="form-control"

              maxDate={endDate || null}

            />


            {/* END DATE */}

            <DatePicker

              selected={endDate}

              onChange={(date) => {

                console.log(
                  "END DATE SELECTED:",
                  date
                );

                console.log(
                  "END LOCAL DATE:",
                  formatLocalDate(date)
                );

                setEndDate(date);
              }}

              dateFormat="yyyy-MM-dd"

              placeholderText="End Date"

              className="form-control"

              minDate={startDate || null}

            />


            {/* FILTER BUTTON */}

            <Button
              variant="info"
              onClick={
                handleDateRangeFilter
              }
            >
              Filter
            </Button>

          </Col>

        </Row>


        {/* =====================================================
            LEVEL + USERNAME
        ===================================================== */}

        <div className="row g-2 mt-3 align-items-center">


          {/* LEVEL */}

          <div className="col-5 col-md-5 d-flex align-items-center">

            <input

              type="number"

              min="1"

              className="form-control"

              placeholder="Level"

              value={level}

              onChange={(e) =>
                setLevel(e.target.value)
              }

              onBlur={() =>
                fetchData(
                  filterType,
                  startDate,
                  endDate
                )
              }

            />

          </div>


          {/* USERNAME */}

          <div className="col-7 col-md-7">

            <input

              type="text"

              className="form-control"

              placeholder="Search by Username"

              value={search}

              onChange={(e) =>
                setSearch(e.target.value)
              }

            />

          </div>

        </div>

      </div>


      {/* =====================================================
          RESULT CARD
      ===================================================== */}

      <div className="card shadow-sm p-3">


        {/* =====================================================
            TEAM TOTALS
        ===================================================== */}

        <div className="row align-items-center flex-nowrap overflow-auto">


          {/* A TEAM */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              A Team:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {sumA.toFixed(2)}

              </span>

            </h5>

          </div>


          {/* B TEAM */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              B Team:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {sumB.toFixed(2)}

              </span>

            </h5>

          </div>


          {/* C TEAM */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              C Team:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {sumC.toFixed(2)}

              </span>

            </h5>

          </div>


          {/* OTHERS */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              Others:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {team_level_others.toFixed(2)}

              </span>

            </h5>

          </div>


          {/* TOTAL */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              Total:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {sumtotal.toFixed(2)}

              </span>

            </h5>

          </div>


          {/* SELECT ACCORDING */}

          <div className="col-6 col-md-3 d-flex align-items-center">

            <h5 className="text-dark">

              Select According:

              <span className="text-warning ms-1">

                {CurrencySymbol}

                {Number(
                  totalAmount || 0
                ).toFixed(2)}

              </span>

            </h5>

          </div>

        </div>


        {/* =====================================================
            TABLE
        ===================================================== */}

        <div className="table-responsive mt-3">

          <table className="table table-striped align-middle">

            <thead className="table-secondary text-dark">

              <tr>

                <th>SL</th>

                <th>Username</th>

                <th>Email</th>

                <th>Invest Amount</th>

                <th>Site</th>

                <th>Status</th>

                <th>Date</th>

              </tr>

            </thead>


            <tbody>

              {/* =================================================
                  LOADING
              ================================================= */}

              {loading ? (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center py-3 text-muted"
                  >
                    Loading...
                  </td>

                </tr>

              ) : searchedData.length > 0 ? (

                /* ===============================================
                   DATA
                =============================================== */

                searchedData.map(
                  (row, i) => (

                    <tr
                      style={{
                        color: "#292929"
                      }}
                      key={
                        row.id
                          ? `${row.id}-${i}`
                          : i
                      }
                    >

                      <td>
                        {i + 1}
                      </td>


                      <td>
                        {row.username}
                      </td>


                      <td>
                        {row.email}
                      </td>


                      <td>

                        {CurrencySymbol}

                        {parseFloat(
                          row.fixt_amount || 0
                        ).toFixed(2)}

                      </td>


                      <td>

                        {row.team_position
                          ? row.team_position.toUpperCase()
                          : "-"}

                      </td>


                      {/* STATUS */}

                      <td>

                        {(() => {

                          switch (
                            Number(row.status)
                          ) {

                            case 0:

                              return (
                                <span className="badge bg-warning">
                                  Inactive
                                </span>
                              );


                            case 1:

                              return (
                                <span className="badge bg-success">
                                  Active
                                </span>
                              );


                            case 2:

                              return (
                                <span className="badge bg-dark">
                                  Refunded
                                </span>
                              );


                            case 3:

                              return (
                                <span className="badge bg-info">
                                  Processing
                                </span>
                              );


                            case 4:

                              return (
                                <span className="badge bg-secondary">
                                  Cancelled
                                </span>
                              );


                            default:

                              return (
                                <span className="badge bg-dark">
                                  Unknown
                                </span>
                              );
                          }

                        })()}

                      </td>


                      {/* DATE */}

                      <td>
                        {row.create_date}
                      </td>

                    </tr>

                  )
                )

              ) : (

                /* ===============================================
                   EMPTY
                =============================================== */

                <tr>

                  <td
                    colSpan="7"
                    className="text-center text-muted py-3"
                  >
                    No records found
                  </td>

                </tr>

              )}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
};

export default TeamInvestReport;